import React, {useEffect, useRef} from 'react';

// @ts-expect-error
import {BusyOverlay} from '@pb/kit-component-busy-overlay';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {Message} from '../message';

import styles from './success-message.module.styl';

import type {MessageProps} from '../message';
import type {FC} from 'react';


export const TIMEOUT = 2000;

const classNames = withCssModule(styles);

/**
 * SuccessMessage Props
 */
export interface SuccessMessageProps extends Omit<MessageProps, 'color'> {
    /**
     * Function will be called when close icon pressed
     */
    onClose: () => void,
    /**
     * Show property for dialog layer wrapper
     */
    shown: boolean,
    /**
     * Overlay classes for success message
     */
    overlayClassName?: string,
    /**
     * Auto close message after a timeout.
     */
    timeout?: number,
}

/**
 * The SuccessMessage component. Shows message on success event.
 * @deprecated It will be removed in 6 months 09-20-2025. Use Push Notification instead. https://storybook.dev.pitchbookdata.com/?path=/docs/components-push-notification--documentation
 */
export const SuccessMessage: FC<SuccessMessageProps> = ({
    timeout = TIMEOUT,
    overlayClassName,
    shown,
    onClose,
    children,
    ...props
}) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const [isShown, setIsShown] = useControlledState(shown, undefined, false);

    const mixClass = classNames('success-message', overlayClassName);

    const handleClose = () => {
        if (timeoutRef?.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsShown(false);
        onClose();
    };

    useEffect(() => {
        if (isShown) {
            timeoutRef.current = setTimeout(() => {
                onClose();
                setIsShown(false);
            }, timeout);
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isShown]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <BusyOverlay shown={isShown} onClose={handleClose} mix={mixClass}>
            <Message
                {...props}
                iconProps={{type: Icons.CHECK, size: Sizes.Size_14}}
                color="success">
                {children}
            </Message>
        </BusyOverlay>
    );
};

SuccessMessage.displayName = 'SuccessMessage';
